"use client";
import React from "react";
import { useSelectedLayoutSegments } from "next/navigation";
import Link from "next/link";
import { useLingui } from "@lingui/react";
import { t, Trans } from "@lingui/macro";

type BreadcrumbProps = {
  layout: string;
};

type SubCrumb = { href: string; text: string; hasId?: boolean };
type Breadcrumb = { crumb: string; crumbRef: string; subCrumbs: SubCrumb[] };

function BreadcrumbMenu({ layout }: Readonly<BreadcrumbProps>): React.JSX.Element {
  const { i18n } = useLingui();
  const segments = useSelectedLayoutSegments().filter((segment) => !segment.startsWith("("));

  const breadcrumbData: Record<string, Breadcrumb> = {
    account: {
      crumb: t(i18n)`Account`,
      crumbRef: "account",
      subCrumbs: [
        { href: "/voucherwallet", text: t(i18n)`Voucher Wallet` },
        { href: "/voucherwallet/order-voucher", text: t(i18n)`Order Voucher`, hasId: true },
        { href: "/transactions", text: t(i18n)`Your Transactions` },
        { href: "/orders", text: t(i18n)`Order History` },
        { href: "/orders/info", text: t(i18n)`Order Information` },
        { href: "/edit-account", text: t(i18n)`Edit Information` },
        { href: "/password", text: t(i18n)`Change Password` },
        { href: "/addresses", text: t(i18n)`Address Book` },
        { href: "/addresses/add", text: t(i18n)`Add Address` },
        { href: "/addresses/edit", text: t(i18n)`Edit Address` },
        { href: "/account", text: t(i18n)`My Account` },
        { href: "/wishlist", text: t(i18n)`My Wish List` },
        { href: "/newsletter", text: t(i18n)`Newsletter` },
        { href: "/offerz-coupons", text: t(i18n)`Offerz Giftcard` },
        { href: "/security", text: t(i18n)`Security` },
      ],
    },
    loyalty: {
      crumb: t(i18n)`Offerz Plus`,
      crumbRef: "loyalty",
      subCrumbs: [
        { href: "/slotmachine", text: t(i18n)`Slotmachine` },
        { href: "/rewards", text: t(i18n)`Rewards` },
        { href: "/stampcards", text: t(i18n)`Stamp cards` },
        { href: "/loyalty", text: t(i18n)`Offerz Plus` },
      ],
    },
    cart: { crumb: t(i18n)`Shopping Cart`, crumbRef: "cart", subCrumbs: [] },
    search: { crumb: t(i18n)`Search`, crumbRef: "search", subCrumbs: [] },
  };

  const { crumb, crumbRef, subCrumbs } = breadcrumbData[layout] ?? {
    crumb: "",
    crumbRef: "/",
    subCrumbs: [],
  };

  const matchByHref = (segment: string) => {
    return subCrumbs.find((link) => link.href === segment);
  };

  return (
    <div className="mt-0 flex items-center overflow-x-auto pb-2.5">
      <Link href="/" data-testid="home-link" prefetch={false}>
        <p className="ease text-sm text-primary transition duration-300 hover:text-black">
          <Trans>Home</Trans>
        </p>
      </Link>

      <div className="mx-4 h-3 w-[1px] shrink-0 bg-[#00000099]" />
      <nav aria-label="breadcrumb" className="overflow-x-auto">
        <ol className="flex gap-3 whitespace-nowrap text-sm">
          <li>
            <Link
              href={`/${crumbRef}`}
              className={`transition-colors duration-300 ${segments.length === 0 ? "text-black" : "text-primary"} hover:text-black`}
              data-testid={`${crumbRef}-link`}
              prefetch={false}
            >
              {crumb}
            </Link>
          </li>

          {segments.length > 0 &&
            segments.map((_segment, index) => {
              const href = `/${segments.slice(0, index + 1).join("/")}`;
              const fullHref = `/${crumbRef}/${segments.slice(0, index + 1).join("/")}`;
              const match = matchByHref(href);
              if (!match?.text) return null;

              const isLast = match?.hasId
                ? index === segments.length - 2
                : index === segments.length - 1;

              return (
                <React.Fragment key={`${match.text}-${index}`}>
                  <li aria-hidden="true" className="text-[#00000099]">
                    ›
                  </li>
                  <li>
                    {isLast ? (
                      <span className="text-black">{match.text}</span>
                    ) : (
                      <Link
                        href={fullHref}
                        className="text-primary transition-colors duration-300 hover:text-black"
                        prefetch={false}
                      >
                        {match.text}
                      </Link>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
        </ol>
      </nav>
    </div>
  );
}
export default React.memo(BreadcrumbMenu);
